import { Link, useRouterState } from "@tanstack/react-router";
import { LayoutDashboard, FileText, Settings, ShieldCheck, Sparkles, BadgeCheck, Wallet, ChevronRight, FolderKanban, MessageSquare, Globe, Monitor, CalendarDays } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { planFor, effectiveLimit, effectiveTier } from "@/lib/plans";
import { BrandLogo } from "@/components/BrandLogo";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

const dashboardItem = { key: "dashboard", url: "/dashboard" as const, icon: LayoutDashboard };
const projectsItem = { key: "projects", url: "/projects" as const, icon: FolderKanban };
const chatItem = { key: "chat", url: "/chat" as const, icon: MessageSquare };
const calendarItem = { key: "calendar", url: "/calendar" as const, icon: CalendarDays };
const invoicesItem = { key: "invoices", url: "/invoices" as const, icon: FileText };
const escrowItem = { key: "escrow", url: "/escrow" as const, icon: ShieldCheck };
const walletItem = { key: "wallet", url: "/wallet" as const, icon: Wallet };
const portfolioItem = { key: "portfolio", url: "/portfolio" as const, icon: Globe };
const pricingItem = { key: "pricing", url: "/plans" as const, icon: Sparkles };

const settingsItem = { key: "settings", url: "/settings" as const, icon: Settings };

export function AppSidebar({ side = "left" }: { side?: "left" | "right" }) {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };
  const collapsed = state === "collapsed";
  const currentPath = useRouterState({ select: (r) => r.location.pathname });

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("has_role", { _user_id: user!.id, _role: "admin" });
      if (error) return false;
      return data as boolean;
    },
    enabled: !!user,
  });

  const isFreelancer = profile?.role === "freelancer";
  const needsVerification = isFreelancer && profile?.kyc_status !== "verified";

  const { data: pendingInvoiceCount = 0 } = useQuery({
    queryKey: ["pending-invoice-count", user?.id, profile?.role, profile?.email],
    enabled: !!user && !!profile,
    refetchInterval: 30_000,
    queryFn: async () => {
      if (!user || !profile) return 0;
      let q = supabase.from("invoices").select("id", { count: "exact", head: true }).eq("status", "pending");
      if (isFreelancer) {
        q = q.eq("freelancer_id", user.id);
      } else {
        const { data: myProjects } = await supabase
          .from("projects")
          .select("id")
          .ilike("client_email", profile.email ?? "");
        const ids = (myProjects ?? []).map((p) => p.id);
        if (ids.length === 0) return 0;
        q = q.in("project_id", ids);
      }
      const { count } = await q;
      return count ?? 0;
    },
  });

  const { data: activeProjectCount = 0 } = useQuery({
    queryKey: ["active-project-count", user?.id, profile?.role, profile?.email],
    enabled: !!user && !!profile,
    refetchInterval: 30_000,
    queryFn: async () => {
      if (!user || !profile) return 0;
      let q = supabase.from("projects").select("id", { count: "exact", head: true }).eq("is_intro", false).neq("status", "completed");
      if (isFreelancer) {
        q = q.eq("freelancer_id", user.id);
      } else {
        q = q.ilike("client_email", profile.email ?? "");
      }
      const { count } = await q;
      return count ?? 0;
    },
  });

  const workspaceNav = [
    dashboardItem,
    projectsItem,
    calendarItem,
    chatItem,
    invoicesItem,
    escrowItem,
    ...(isFreelancer ? [walletItem, portfolioItem] : []),
  ];
  const accountNav = [
    ...(needsVerification ? [{ key: "verify", url: "/verify" as const, icon: BadgeCheck }] : []),
    ...(isFreelancer ? [pricingItem] : []),
    { key: "devices", url: "/devices" as const, icon: Monitor },
    settingsItem,
    ...(isAdmin ? [{ key: "admin", url: "/admin" as const, icon: ShieldCheck }] : []),
  ];

  const isActive = (path: string) => currentPath === path;

  const activeTier = effectiveTier(profile);
  const plan = profile ? planFor(activeTier) : null;
  const planName = plan ? t(`pricingPage.plans.${plan.id}.name`) : "";
  const used = profile?.projects_created_this_month ?? 0;
  const bonus = profile?.projects_bonus_slots ?? 0;
  const effLimit = profile ? effectiveLimit(activeTier, bonus) : null;
  const planPct = effLimit ? Math.min(100, (used / effLimit) * 100) : 0;
  const planNear = planPct >= 80;

  const renderItem = (item: { key: string; url: string; icon: React.ComponentType<{ className?: string; strokeWidth?: number | string }> }) => {
    const label = t(`nav.${item.key}`, { defaultValue: item.key.charAt(0).toUpperCase() + item.key.slice(1) });
    const badge =
      item.key === "invoices" && pendingInvoiceCount > 0
        ? pendingInvoiceCount
        : item.key === "projects" && activeProjectCount > 0
          ? activeProjectCount
          : 0;
    const active = isActive(item.url);
    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton
          asChild
          isActive={active}
          tooltip={collapsed ? (badge ? `${label} (${badge})` : label) : undefined}
          className={cn(
            "group relative h-9 rounded-lg px-2 transition-all duration-300 ease-out",
            active
              ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] text-sidebar-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.3)]"
              : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/[0.03]"
          )}
        >
          <Link to={item.url} onClick={closeOnMobile} className="relative flex items-center gap-2.5">
            {active && !collapsed && (
              <span
                aria-hidden
                className="absolute -start-1 top-1/2 h-3.5 w-[2px] -translate-y-1/2 rounded-full bg-primary shadow-[0_0_8px_color-mix(in_oklab,var(--primary)_60%,transparent)]"
              />
            )}
            <span
              aria-hidden
              className={cn(
                "relative grid h-6 w-6 shrink-0 place-items-center rounded-[7px] transition-all duration-300",
                active
                  ? "bg-[linear-gradient(160deg,color-mix(in_oklab,var(--primary)_85%,transparent),color-mix(in_oklab,var(--primary)_55%,transparent))] ring-1 ring-white/[0.14] shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_2px_6px_color-mix(in_oklab,var(--primary)_35%,transparent)]"
                  : "bg-[linear-gradient(160deg,rgba(255,255,255,0.05),rgba(255,255,255,0.015))] ring-1 ring-white/[0.06] group-hover:ring-white/[0.10] group-hover:bg-[linear-gradient(160deg,rgba(255,255,255,0.07),rgba(255,255,255,0.025))]"
              )}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-1 top-0 h-px rounded-full bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-70"
              />
              <item.icon
                strokeWidth={active ? 2 : 1.75}
                className={cn(
                  "h-[13px] w-[13px] transition-colors duration-300",
                  active
                    ? "text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]"
                    : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground"
                )}
              />
            </span>
            <span
              className={cn(
                "flex-1 truncate text-[13px] tracking-[-0.01em] transition-colors",
                active ? "font-medium text-sidebar-foreground" : "font-normal"
              )}
            >
              {label}
            </span>
            {badge > 0 && !collapsed && (
              <span
                className={cn(
                  "ms-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold leading-none tabular-nums transition-colors",
                  active
                    ? "bg-primary text-primary-foreground shadow-[0_0_10px_color-mix(in_oklab,var(--primary)_50%,transparent)]"
                    : "bg-white/[0.08] text-sidebar-foreground/80"
                )}
              >
                {badge > 99 ? "99+" : badge}
              </span>
            )}
            {badge > 0 && collapsed && (
              <span className="absolute end-1 top-1 h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_color-mix(in_oklab,var(--primary)_60%,transparent)]" />
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };



  return (
    <Sidebar collapsible="icon" variant="sidebar" side={side}>
      <SidebarHeader className={cn("py-4", collapsed ? "px-2" : "px-3")}>
        <Link
          to="/dashboard"
          onClick={closeOnMobile}
          aria-label="Lumvio — dashboard"
          className={cn(
            "group relative flex items-center gap-3 rounded-2xl transition-all duration-500 ease-out",
            "border border-white/[0.06] bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))]",
            "shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.35)]",
            "hover:border-white/[0.12] hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))]",
            collapsed ? "justify-center p-2" : "px-3 py-2.5"
          )}
        >
          {/* Specular top highlight — Apple hardware sheen */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
          />

          {/* Logo tile */}
          <span className="relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-[10px] bg-[linear-gradient(160deg,#1a1a1c_0%,#0a0a0b_100%)] ring-1 ring-white/[0.08] shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_2px_6px_rgba(0,0,0,0.45)]">
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[10px] bg-[radial-gradient(80%_50%_at_50%_0%,rgba(255,255,255,0.14),transparent_70%)]"
            />
            <img
              src="/logo.png"
              alt="Lumvio"
              className="relative h-5 w-5 object-contain"
              loading="eager"
              decoding="async"
            />
          </span>

          {!collapsed && (
            <span className="relative flex min-w-0 flex-col leading-none">
              <span className="text-[15px] font-semibold tracking-[-0.015em] text-sidebar-foreground/95">
                Lumvio
              </span>
              <span className="mt-1 text-[10px] font-normal tracking-[0.02em] text-sidebar-foreground/45">
                {t("nav.workspace")}
              </span>
            </span>
          )}

          {!collapsed && (
            <span
              aria-hidden
              className="ms-auto h-1.5 w-1.5 rounded-full bg-emerald-400/80 shadow-[0_0_8px_rgba(52,211,153,0.6)]"
            />
          )}
        </Link>
      </SidebarHeader>


      <SidebarContent>
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="text-[10px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground/40">
              {t("nav.workspace")}
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">{workspaceNav.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {accountNav.length > 0 && (
          <SidebarGroup>
            {!collapsed && (
              <SidebarGroupLabel className="text-[10px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground/40">
                {t("nav.account", { defaultValue: "Account" })}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">{accountNav.map(renderItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isFreelancer && plan && !collapsed && (
          <SidebarGroup className="mt-auto">
            <SidebarGroupContent className="px-2">
              <Link
                to="/plans"
                onClick={closeOnMobile}
                className="group relative block overflow-hidden rounded-xl border border-white/[0.06] bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))] p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.35)] transition-all duration-500 hover:border-white/[0.12] hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className={cn("h-1.5 w-1.5 rounded-full", planNear ? "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]" : "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.55)]")} />
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/80">
                      {planName}
                    </span>
                  </div>
                  {planNear && effLimit !== null && (
                    <span className="text-[10px] font-semibold tabular-nums text-amber-400">
                      {Math.round(planPct)}%
                    </span>
                  )}
                </div>
                <div className="mt-2 text-[11.5px] tracking-[-0.005em] text-sidebar-foreground/55">
                  {effLimit === null
                    ? t("sidebar.projectsThisMonth", { used })
                    : t("sidebar.projectsUsed", { used, limit: effLimit })}
                </div>
                {effLimit !== null && (
                  <div className="mt-2.5 h-[3px] w-full overflow-hidden rounded-full bg-white/[0.06]">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-700",
                        planNear
                          ? "bg-gradient-to-r from-amber-500 to-amber-300"
                          : "bg-gradient-to-r from-primary to-primary/70"
                      )}
                      style={{ width: `${planPct}%` }}
                    />
                  </div>
                )}
                {plan.id !== "unlimited" && (
                  <div className="mt-3 inline-flex items-center gap-0.5 text-[11.5px] font-medium tracking-[-0.005em] text-primary">
                    {t("common.upgrade")}
                    <ChevronRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </div>
                )}
              </Link>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

      </SidebarContent>

    </Sidebar>
  );
}
